import React, {useState} from 'react'
import {useParams} from "react-router-dom";
import {useQuery} from "react-query";
import {
  createTestQuestion,
  getQuestionById,
  getTestQuestions,
  IQuestionItem,
  updateTestQuestion
} from "../../../../services/testQuestionSvc";
import {Pagination} from "../../../../components/common/pagination/Pagination";
import {FieldArray, FormikErrors, FormikProvider, useFormik} from "formik";
import {LANGUAGES} from "../../../../mock/languages";
import {lang} from "../../../../types/lang";
import {useNotification} from "../../../../context/NotificationContext";
import {KTIcon} from "../../../../../_metronic/helpers";
import {useDeleteModal} from "../../../../context/DeleteModalContext";
import {MODERATOR_TEST_QUESTION} from "../../../../mock/URLs";
import * as Yup from "yup";
import {EmptySection} from "../../../../components/sections/empty/Empty";

const createTestQuestionSchema = Yup.object().shape({
  questions: Yup.array().of(
      Yup.object().shape({
        question: Yup.object().shape({
          uz: Yup.string().required("Majburiy"),
          ru: Yup.string().required("Majburiy"),
          en: Yup.string().required("Majburiy"),
        }),
        answers: Yup.array().of(
            Yup.object().shape({
              answer: Yup.object().shape({
                uz: Yup.string().required("Majburiy"),
                ru: Yup.string().required("Majburiy"),
                en: Yup.string().required("Majburiy"),
              }),
              ball: Yup.number().required("Majburiy"),
            })
        ),
      })
  ),
});

export const TestQuestionCreatePage = () => {
  const {testId} = useParams()
  const [activeTab, setActiveTab] = useState<lang>("uz");
  const {showNotification} = useNotification();
  const {
    data: testData,
    refetch: refetchTest
  } = useQuery("getTestQuestions", () => getTestQuestions({params: {test: Number(testId!)}}), {
    enabled: !!testId
  })

  const [selectedQuestion, setSelectedQuestion] = useState<number | null>(null)
  const {data: questionById} = useQuery(["getQuestionById", selectedQuestion], () => getQuestionById(selectedQuestion!), {
    enabled: !!selectedQuestion
  })

  const totalItems = testData?.total_items || 0;
  const itemsPerPage = testData?.page_size || 8;

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: createTestQuestionSchema,
    initialValues: {
      questions: [
        {
          test: questionById?.test || testId,
          question: {
            uz: questionById?.question_uz || "",
            ru: questionById?.question_ru || "",
            en: questionById?.question_en || ""
          },
          is_active: questionById?.is_active || true,
          answers: questionById?.answers.map(answer => ({
            ...(selectedQuestion ? {id: answer.id} : {}),
            answer: {
              uz: answer.answer_uz || "",
              ru: answer.answer_ru || "",
              en: answer.answer_en || ""
            },
            type: answer.type,
            ball: answer.ball,
            is_correct: answer.is_correct
          })) || [
            {
              answer: {
                uz: "",
                ru: "",
                en: ""
              },
              type: "RADIO",
              ball: 0,
              is_correct: false
            }
          ]
        }
      ]
    },
    onSubmit: async (values, {resetForm}) => {
      const formattedValues = values.questions.map(question => ({
        test: question.test,
        question_uz: question.question.uz,
        question_ru: question.question.ru,
        question_en: question.question.en,
        is_active: question.is_active,
        answers: question.answers.map(answer => ({
          id: answer?.id,
          answer_uz: answer.answer.uz,
          answer_ru: answer.answer.ru,
          answer_en: answer.answer.en,
          type: answer.type,
          ball: answer.ball,
          is_correct: answer.is_correct
        }))
      }));
      try {
        if (selectedQuestion) {
          const res = await updateTestQuestion(Number(selectedQuestion), formattedValues[0]);
          if (res.success) {
            showNotification("Muvaffaqiyatli", "Savollar yangilandi", "success");
            selectedQuestion && setSelectedQuestion(null);
            resetForm();
            refetchTest();
          }
        } else {
          const res = await createTestQuestion(formattedValues as IQuestionItem[]);
          if (res.success) {
            showNotification("Muvaffaqiyatli", "Savollar qo'shildi", "success");
            selectedQuestion && setSelectedQuestion(null);
            refetchTest();
            resetForm();
          }
        }
      } catch (e) {
        showNotification("Xatolik!", "", "error");
      }
    }
  });

  const handleTypeChange = (index: number, type: string) => {
    const updatedQuestions = [...formik.values.questions];
    updatedQuestions[index].answers = updatedQuestions[index].answers.map(answer => ({
      ...answer,
      type: type
    }));
    formik.setFieldValue('questions', updatedQuestions);
  }

  const handleCorrectAnswerChange = (qIndex: number, aIndex: number, type: string) => {
    const updatedQuestions = [...formik.values.questions];
    if (type === "CHECKBOX") {
      updatedQuestions[qIndex].answers[aIndex].is_correct = !updatedQuestions[qIndex].answers[aIndex].is_correct;
    } else {
      updatedQuestions[qIndex].answers = updatedQuestions[qIndex].answers.map((answer, index) => ({
        ...answer,
        is_correct: index === aIndex
      }));
    }
    formik.setFieldValue('questions', updatedQuestions);
  }

  const handleTabChange = (lang) => {
    setActiveTab(lang);
  };

  const {openDeleteModal} = useDeleteModal()

  const handleDeleteClick = (id: number) => {
    openDeleteModal(MODERATOR_TEST_QUESTION, id, refetchTest);
  };


  return (
      <div>
        <div className="card mb-10">
          <div className="card-header">
            <h1 className="card-title">Savol qo'shish</h1>
            <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
              {LANGUAGES.map((lang) => (
                  <li className="nav-item" key={lang.key}>
                    <button
                        className={`nav-link ${activeTab === lang.key ? 'active' : ''}`}
                        onClick={() => handleTabChange(lang.key)}
                    >
                      {lang.name}
                    </button>
                  </li>
              ))}
            </ul>
          </div>
          <div className="card-body">
            <FormikProvider value={formik}>
              <FieldArray name="questions">
                {({push, remove}) => (
                    <div>
                      {formik.values.questions.map((question, index) => (
                          <div key={index} className="mb-10 p-5 bg-gray-100 rounded-4">
                            <div>
                              <div className="d-flex justify-content-between gap-10">
                                <div className="d-flex gap-2 w-100">
                                  <h3>{index + 1}.</h3>
                                  <textarea
                                      className="form-control w-100"
                                      name={`questions.${index}.question.${activeTab}`}
                                      value={question.question?.[activeTab]}
                                      onChange={formik.handleChange}
                                      placeholder="Savol kiriting"
                                  />
                                </div>
                                <div className="d-flex justify-content-end gap-2 ml-3">
                                  <button type="button" className="btn btn-icon btn-danger"
                                          onClick={() => remove(index)}>
                                    <i className="ki-solid ki-trash-square fs-2x"></i>
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="d-flex mt-5">
                              <select
                                  className={"form-select w-100"}
                                  name={`questions.${index}.type`}
                                  value={question.answers[0].type}
                                  onChange={(e) => handleTypeChange(index, e.target.value)}
                              >
                                <option value="RADIO">Bir tanlovlik</option>
                                <option value="CHECKBOX">Ko'p tanlovlik</option>
                              </select>
                            </div>
                            <div className="d-flex mt-5">
                              <ul className="list-group w-100">
                                <FieldArray name={`questions.${index}.answers`}>
                                  {({push: pushAnswer, remove: removeAnswer}) => (
                                      <div>
                                        <div className={"rounded"}>
                                          {question.answers.map((answer, ansIndex) => (
                                              <li key={ansIndex}
                                                  className={`list-group-item ${answer.is_correct ? 'text-success fw-bold' : ''}`}>
                                                <div>
                                                  <div className="d-flex gap-10 justify-content-between">
                                                    <div className={"d-flex align-items-center gap-3 w-100"}>
                                                      <div
                                                          className="form-check form-check-custom form-check-solid">
                                                        <input
                                                            className={"form-check-input"}
                                                            type={answer.type.toLowerCase()}
                                                            name={`questions.${index}.correct_answer`}
                                                            checked={answer.is_correct}
                                                            onChange={() => handleCorrectAnswerChange(index, ansIndex, answer.type)}
                                                        />
                                                      </div>

                                                      <textarea
                                                          className={"form-control w-100"}
                                                          name={`questions.${index}.answers.${ansIndex}.answer.${activeTab}`}
                                                          value={answer.answer?.[activeTab]}
                                                          onChange={formik.handleChange}
                                                          placeholder="Javob kiriting"/>

                                                    </div>
                                                    <div className={"d-flex align-items-center gap-2"}>
                                                  <span><input type="number"
                                                               placeholder={"Ball"}
                                                               className={"form-control w-100"}
                                                               name={`questions.${index}.answers.${ansIndex}.ball`}
                                                               value={answer.ball} onChange={formik.handleChange}
                                                               style={{width: '50px'}}/></span>
                                                      <div className="d-flex gap-2">
                                                        <button type="button" className="btn btn-icon btn-light-danger"
                                                                onClick={() => removeAnswer(ansIndex)}>
                                                          <KTIcon iconName={"trash-square"} className="fs-2x"/>
                                                        </button>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </li>
                                          ))}
                                        </div>
                                        <button type="button" className="btn btn-sm mt-5 btn-light-primary"
                                                onClick={() => pushAnswer({
                                                  answer: {
                                                    uz: "",
                                                    ru: "",
                                                    en: "",
                                                  },
                                                  type: question.answers[0].type,
                                                  ball: 0,
                                                  is_correct: false
                                                })}>Yangi javob qo'shish
                                        </button>
                                      </div>
                                  )}
                                </FieldArray>
                              </ul>
                            </div>
                          </div>
                      ))}
                      {
                          !selectedQuestion && <div>
                              <button type="button" className="btn btn-sm btn-primary" onClick={() => push({
                                test: testId,
                                question: {
                                  uz: "",
                                  ru: "",
                                  en: ""
                                },
                                is_active: true,
                                answers: [
                                  {
                                    answer: {
                                      uz: "",
                                      ru: "",
                                      en: ""
                                    }, type: "RADIO", ball: 0, is_correct: false
                                  }
                                ]
                              })}>Yangi savol qo'shish
                              </button>
                          </div>
                      }
                    </div>
                )}
              </FieldArray>
              <div className={"text-end mt-10"}>
                <button onClick={() => formik.handleSubmit()} className={"btn btn-primary"} type={"submit"}>Saqlash
                </button>
              </div>
            </FormikProvider>
          </div>
        </div>

        <div className="card pb-10 mb-10">
          <div className="card-header"><h1 className="card-title">Mavjud savollar</h1></div>
          <div className="card-body">
            <div>
              {
                testData?.data?.map((question, index) => (
                    <div className="mb-10 p-5 bg-gray-100 rounded-4" key={question.id}>
                      <div className="d-flex justify-content-between"><h3>{index + 1}. {question?.question}</h3>
                        <div className="d-flex w-25 justify-content-end gap-2">
                          <button onClick={() => setSelectedQuestion(question?.id!)} type="button"
                                  className="btn btn-icon btn-light-primary"><i
                              className="ki-solid ki-pencil fs-1"></i></button>
                          <button onClick={() => handleDeleteClick(question?.id!)} type="button"
                                  className="btn btn-icon btn-danger"><i
                              className="ki-solid ki-trash-square fs-2x"></i></button>
                        </div>
                      </div>
                      <div className="d-flex mt-5">
                        <ul className="list-group w-100">
                          {
                            question?.answers?.map((answer, index) => (
                                <li className={`list-group-item ${answer.is_correct ? 'text-success' : 'false'}`}
                                    key={index}>
                                  <div className="d-flex justify-content-between">
                                    <label htmlFor={`answer-${question.id}-${index}`}>{answer.answer}</label>
                                    <span>Ball: {answer.ball}</span>
                                  </div>
                                </li>
                            ))
                          }
                        </ul>
                      </div>
                    </div>
                ))
              }
            </div>
          </div>

          {Boolean(totalItems) ? <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
          /> : <EmptySection height={"30vh"}/>}
        </div>
      </div>
  )
}